import { defineComponent, ref, Ref, onMounted, PropType, watch } from 'vue';
import { HoursOfOperationHolidaysCondition, IHoursOfOperationManager} from '@truckdown/locations';
import { formatDate } from '../../../utilities';
import context from '@truckdown/components-systems';
interface HolidayModel {
    id: string;
    name: string;
    month?: number;
    day?: number;
    majorHoliday: boolean;
    regionCodes: string[];
    countryCodes: string[];
    relativeDate: boolean;
    relativeDateConditions: HolidayRelativeConditionModel[];
    displayDate: string;
}
interface HolidayRelativeConditionModel {
    type: HolidayConditionType;
    value?: number;
}
export declare enum HolidayConditionType {
    Month = 0,
    DayOfWeek = 1,
    LastBeforeDay = 2,
    DayOfWeekInMonth = 3
}
interface IData {
    allHolidays: Ref<HolidayModel[]>,
    allCountryHolidays: Ref<HolidayModel[]>,
    holidaysModel: Ref<{ [id: string]: HoursOfOperationHolidaysCondition }>,
    isLoading: Ref<boolean>
}

const getHolidaysModel = function (
    holidays: { id: string; majorHoliday?: boolean }[],
   
    status: HoursOfOperationHolidaysCondition,
    current?: { [id: string]: HoursOfOperationHolidaysCondition },
) {
    return holidays.reduce((acc, holiday) => {
        if (holiday.majorHoliday || current == undefined) {
            acc[holiday.id] = status;
        } else {
            acc[holiday.id] = current[holiday.id];
        }
        return acc;
    }, {} as Record<string, HoursOfOperationHolidaysCondition>);
};


//const updateModelCondition = function (holidays: { [id: string]: HoursOfOperationHolidaysCondition }, condition: HoursOfOperationHolidaysCondition) {
//    return Object.fromEntries(
//        Object.keys(holidays).map(key => [
//            key,
//            condition
//        ]))
//}
const updateModelCondition = function (
    holidays: { id: string; majorHoliday?: boolean }[],
    existing: { [id: string]: HoursOfOperationHolidaysCondition },
    condition: HoursOfOperationHolidaysCondition
) {
    return Object.fromEntries(
        holidays.map(h => [
            h.id,
            h.majorHoliday === true ? condition : existing[h.id]
        ])
    );
};

const getInitialData = function (props: any): IData {
    return {
        allHolidays: ref<HolidayModel[]>([]),
        allCountryHolidays: ref<HolidayModel[]>([]),
        holidaysModel: ref<{ [id: string]: HoursOfOperationHolidaysCondition }>({}),
        isLoading: ref<boolean>(false)
    };
}

const getComputed = function (props: any, data: IData) {
    return {

    };
}

const getHolidays = async function (data: IData, countryCode: string) {
    if (!data.isLoading.value) {
        data.isLoading.value = true;
        try {
            const manager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            const result = await manager.getHolidays();

            if (result.data) {
                const code = countryCode?.trim().toUpperCase();

                data.allHolidays.value = result.data
                    .filter(h =>
                        !code || h.countryCodes?.includes(code)
                    )
                    .sort((a, b) =>
                        new Date(a.displayDate).getTime() - new Date(b.displayDate).getTime()
                    );
            }

        } catch (err) {
            console.error(err)
        } finally {
            data.isLoading.value = false;
        }
    }
}

const getMethods = function (data: IData, ctx: any, props: any) {
    return {
        getRegions: function (regions: string[]) {
            return regions.join(", ");
        },
        getCountries: function(codes: string[]): string {
            const countryMap: Record<string, string> = {
                CA: "Canada",
                US: "United States",
            };

            return codes
                .map(code => countryMap[code])
                .filter(Boolean)
                .join(", ");
        },
        updateHoursType: function () {
            ctx.emit('update-holidays', data.holidaysModel.value)
        }
    };
}

export default defineComponent({
    name: "HolidayHours",
    props: {
        holidays: {
            type: Object,
            required: true,
            default: {}
        },
        condition: {
            type: Number as PropType<HoursOfOperationHolidaysCondition>,
            required: true,
        },
        countryCode: {
            type: String,
            required: true
        }
    },
    emits: ['update-holidays'],
    setup: function (props, ctx) {
        var data = getInitialData(props);
        var computed = getComputed(props, data);
        var methods = getMethods(data, ctx, props);

        onMounted(async () => {
            await getHolidays(data, "");
            data.allCountryHolidays.value = data.allHolidays.value.filter(h => !props.countryCode || h.countryCodes?.includes(props.countryCode));
            if (Object.keys(props.holidays).length == 0) {
                data.holidaysModel.value = getHolidaysModel(data.allCountryHolidays.value, props.condition);
            } else { 
                data.holidaysModel.value = props.holidays;
            }
        })

        watch(() => props.condition,
            (newVal, oldVal) => {
                if (newVal != undefined) {
                    data.allCountryHolidays.value = data.allHolidays.value.filter(h => !props.countryCode || h.countryCodes?.includes(props.countryCode));
                if (Object.keys(props.holidays).length === 0) {
                    data.holidaysModel.value = getHolidaysModel(data.allCountryHolidays.value, newVal, data.holidaysModel.value);
                } else {
                    data.holidaysModel.value = updateModelCondition(data.allCountryHolidays.value, props.holidays, newVal);
                }
                ctx.emit('update-holidays', data.holidaysModel.value);
            }
        }, {
            immediate: true
        });

        watch(
            () => props.countryCode,
            async (newVal, oldVal) => {
                if (newVal !== undefined) {
                    data.allCountryHolidays.value = data.allHolidays.value.filter(h => !newVal || h.countryCodes?.includes(newVal));
                    // update holidays by code
                    if (Object.keys(props.holidays).length === 0) {
                        data.holidaysModel.value = getHolidaysModel(
                            data.allCountryHolidays.value,
                            props.condition,
                            data.holidaysModel.value
                        );
                    } else {
                        data.holidaysModel.value = props.holidays;
                    }
                }
            }
        );


        watch(
            () => [
                props.holidays,
            ],
            ([newVal, originalVal]) => {
                const newValue = newVal ?? originalVal ?? undefined;
                if (newVal != undefined) {
                    data.allCountryHolidays.value = data.allHolidays.value.filter(h => !props.countryCode || h.countryCodes?.includes(props.countryCode));
                    if (Object.keys(newValue).length == 0) {
                        data.holidaysModel.value = getHolidaysModel(data.allCountryHolidays.value, props.condition, data.holidaysModel.value);
                    } else {
                        data.holidaysModel.value = newVal;
                    }
                }
            },
            {
                deep: true
            }
        );

        return {
            ...data,
            ...computed,
            ...methods,
            'HoursOfOperationHolidaysCondition': HoursOfOperationHolidaysCondition,
            'dateDisplay': formatDate
        }

    }
});

